import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import { Box, Flex } from '@theme-ui/components';
import cookie from 'js-cookie';
import { gql, useLazyQuery } from '@apollo/client';

import { Button } from '@app/components/Button';
import { CustomSelect } from '@app/components/CustomSelect/CustomSelect';
import Loader from '@app/components/Loader';
import type {
  ShopStoreCodeQuery,
  ShopStoreCodeQueryVariables,
} from '@app/context/__generated__/ShopStoreCodeQuery';
import {
  mapToShopLocale,
  SHOP_STORE_CODE_COOKIE,
  type Language,
} from '@app/helpers/localization';
import useLocalizationCountry from '@app/hooks/useLocalizationCountry';
import { COOKIE_DOMAIN } from '@config/client.config';

import type { Option } from './StoreSwitch';
import messages from './StoreSwitch.messages';
import { Label, Wrapper } from './StoreSwitch.styles';

const StoreSwitchPopover: FC<{
  isLoading: boolean;
  options?: Option[];
  currentCountry?: Option;
  close: () => void;
}> = ({ isLoading, options, currentCountry, close }) => {
  const { language } = useParams<{ language: Language }>();
  const locale = mapToShopLocale(language);
  const { setLocalizationCountry } = useLocalizationCountry();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [selectedCountryCode, setSelectedCountryCode] = useState(
    currentCountry?.value,
  );

  const [getStoreData, { error: storeCodeError, data: storeCodeData }] =
    useLazyQuery<ShopStoreCodeQuery, ShopStoreCodeQueryVariables>(
      SHOP_STORE_CODE_QUERY,
      {
        variables: { country: currentCountry?.value || 'US', language: locale },
        fetchPolicy: 'cache-first',
        ssr: false,
      },
    );
  const [store] = storeCodeData?.magento2?.countryStoreCode?.allStores ?? [];
  const { storeCode } = store ?? {};

  const switchToSelectedStore = (countryCode: string) => {
    setLocalizationCountry(countryCode);
    getStoreData({
      variables: { country: countryCode || 'US', language: locale },
    });
  };

  useEffect(() => {
    if (storeCodeError) throw new Error(storeCodeError.message);

    if (storeCode) {
      cookie.set(SHOP_STORE_CODE_COOKIE, storeCode, {
        domain: COOKIE_DOMAIN,
      });
      const url = window.location.href;

      if (url.match(/shop/gi)) {
        window.location.replace(`/${language}/catalog/`);
      } else {
        window.location.reload();
      }
    }
  }, [language, locale, storeCode, storeCodeError]);

  const handleClick = () => {
    if (selectedCountryCode && selectedCountryCode !== currentCountry?.value) {
      switchToSelectedStore(selectedCountryCode);
      setIsRedirecting(true);
    } else {
      close();
    }
  };

  return (
    <Wrapper data-testid="store-switch-popover">
      <Label htmlFor="country-select">
        <FormattedMessage {...messages.location} />
      </Label>

      <Box my={3}>
        {isLoading && (
          <Flex sx={{ justifyContent: 'center' }}>
            <Loader />
          </Flex>
        )}

        {!isLoading && options && (
          <CustomSelect
            data-testid="store-switch-selector"
            id="country-select"
            options={options}
            defaultValue={currentCountry}
            placeholder={currentCountry?.label}
            onChange={(option) => {
              const newOptionValue = (option as Option).value;
              if (newOptionValue) setSelectedCountryCode(newOptionValue);
            }}
          />
        )}
      </Box>

      <Button
        data-testid="store-switch-confirm"
        onClick={handleClick}
        isLoading={isRedirecting}
        type="primary"
        fullWidth
      >
        <FormattedMessage {...messages.confirmButton} />
      </Button>
    </Wrapper>
  );
};

export default StoreSwitchPopover;

const SHOP_STORE_CODE_QUERY = gql`
  query ShopStoreCodeQuery($country: String!, $language: String!) {
    magento2 {
      countryStoreCode(country_code: $country, language: $language) {
        allStores {
          storeId
          storeCode
        }
      }
    }
  }
`;
